import { Dispatch } from "redux";

import {
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	ListPagePostRequest
} from "@/Contracts/UI";
import { TemplatesResponse } from "@/Contracts/Templates";
import { ZomentumEntities } from "@/Contracts";
import { GlobalState } from "@/Reducers/interface";
import { getTemplateListPost } from "@/Services/Templates";
import {
	getAllViewsForEntity,
	getViewMetadataForEntity,
	createViewForEntity,
	updateViewForEntity,
	deleteViewForEntity
} from "@/Services/Views";
import { areFiltersChanged } from "@/Utils";

export enum TemplatesListActionTypes {
	SHOW_TEMPLATES_LIST_LOADER = "TEMPLATES_LIST_SHOW_LOADER",
	HIDE_TEMPLATES_LIST_LOADER = "TEMPLATES_LIST_HIDE_LOADER",

	FETCH_TEMPLATES_LIST_REQUESTED = "TEMPLATES_LIST_FETCH_REQUESTED",
	FETCH_TEMPLATES_LIST_SUCCESSFUL = "TEMPLATES_LIST_FETCH_SUCCESSFUL",
	FETCH_TEMPLATES_LIST_FAILED = "TEMPLATES_LIST_FETCH_FAILED",

	FETCH_TEMPLATES_LIST_VIEWS_SUCCESSFUL = "TEMPLATES_LIST_FETCH_VIEWS_SUCCESSFUL",
	FETCH_TEMPLATES_LIST_VIEW_METADATA_SUCCESSFUL = "TEMPLATES_LIST_FETCH_VIEW_METADATA_SUCCESSFUL",

	CREATE_TEMPLATES_LIST_VIEW_SUCCESSFUL = "TEMPLATES_LIST_CREATE_VIEW_SUCCESSFUL",
	UPDATE_TEMPLATES_LIST_VIEW_SUCCESSFUL = "TEMPLATES_LIST_UPDATE_VIEW_SUCCESSFUL",
	DELETE_TEMPLATES_LIST_VIEW_SUCCESSFUL = "TEMPLATES_LIST_DELETE_VIEW_SUCCESSFUL",

	UPDATE_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW = "TEMPLATES_LIST_UPDATE_CURRENT_ACTIVE_VIEW",
	RESET_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW = "TEMPLATES_LIST_RESET_CURRENT_ACTIVE_VIEW"
}

interface ShowTemplatesListLoader {
	type: TemplatesListActionTypes.SHOW_TEMPLATES_LIST_LOADER;
}
interface HideTemplatesListLoader {
	type: TemplatesListActionTypes.HIDE_TEMPLATES_LIST_LOADER;
}
interface FetchTemplatesListRequested {
	type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_REQUESTED;
}
interface FetchTemplatesListSuccessful {
	type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_SUCCESSFUL;
	templateList: TemplatesResponse;
}
interface FetchTemplatesListFailed {
	type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_FAILED;
}
interface FetchTemplatesListViewsSuccessful {
	type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_VIEWS_SUCCESSFUL;
	viewList: CustomViewResponse;
}
interface FetchTemplatesListViewMetadataSuccessful {
	type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_VIEW_METADATA_SUCCESSFUL;
	metadata: CustomViewsMetadata;
}
interface CreateTemplatesListViewSuccessful {
	type: TemplatesListActionTypes.CREATE_TEMPLATES_LIST_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface UpdateTemplatesListViewSuccessful {
	type: TemplatesListActionTypes.UPDATE_TEMPLATES_LIST_VIEW_SUCCESSFUL;
	view: CustomView;
}
interface DeleteTemplatesListViewSuccessful {
	type: TemplatesListActionTypes.DELETE_TEMPLATES_LIST_VIEW_SUCCESSFUL;
	deletedView: CustomView;
	initialView: CustomView;
}
interface UpdateTemplatesListCurrentActiveView {
	type: TemplatesListActionTypes.UPDATE_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW;
	currentActiveView: CustomView;
	isViewChanged: boolean;
}
interface ResetTemplatesListCurrentActiveView {
	type: TemplatesListActionTypes.RESET_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW;
}

export type TemplatesListActions =
	| ShowTemplatesListLoader
	| HideTemplatesListLoader
	| FetchTemplatesListRequested
	| FetchTemplatesListSuccessful
	| FetchTemplatesListFailed
	| FetchTemplatesListViewsSuccessful
	| FetchTemplatesListViewMetadataSuccessful
	| CreateTemplatesListViewSuccessful
	| UpdateTemplatesListViewSuccessful
	| DeleteTemplatesListViewSuccessful
	| UpdateTemplatesListCurrentActiveView
	| ResetTemplatesListCurrentActiveView;

let nextTemplatesListRequestId = 0;
let latestActiveTemplatesListRequestId = 0;

export const TemplatesListActionCreators = {
	showTemplatesListLoader: () => {
		return (dispatch: Dispatch): ShowTemplatesListLoader =>
			dispatch({
				type: TemplatesListActionTypes.SHOW_TEMPLATES_LIST_LOADER
			});
	},

	hideTemplatesListLoader: () => {
		return (dispatch: Dispatch): HideTemplatesListLoader =>
			dispatch({
				type: TemplatesListActionTypes.HIDE_TEMPLATES_LIST_LOADER
			});
	},

	fetchTemplatesList: (templateRequest: ListPagePostRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchTemplatesListSuccessful | void> => {
			const requestId = ++nextTemplatesListRequestId;
			latestActiveTemplatesListRequestId = requestId;
			dispatch({
				type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_REQUESTED
			});
			try {
				const response = await getTemplateListPost(templateRequest);
				if (requestId !== latestActiveTemplatesListRequestId) {
					return;
				}
				const successful: FetchTemplatesListSuccessful = {
					type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_SUCCESSFUL,
					templateList: response
				};
				return dispatch(successful);
			} catch (error) {
				if (requestId !== latestActiveTemplatesListRequestId) {
					return;
				}
				dispatch({
					type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_FAILED
				});
				return Promise.reject(error);
			}
		};
	},

	fetchAllTemplatesListViews: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchTemplatesListViewsSuccessful> => {
			try {
				const response = await getAllViewsForEntity(
					ZomentumEntities.VendorTemplate
				);
				const successful: FetchTemplatesListViewsSuccessful = {
					type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_VIEWS_SUCCESSFUL,
					viewList: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	fetchTemplatesListViewMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchTemplatesListViewMetadataSuccessful> => {
			try {
				const response = await getViewMetadataForEntity(
					ZomentumEntities.VendorTemplate
				);
				const successful: FetchTemplatesListViewMetadataSuccessful = {
					type: TemplatesListActionTypes.FETCH_TEMPLATES_LIST_VIEW_METADATA_SUCCESSFUL,
					metadata: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	updateCurrentActiveView: (
		selectedCurrentActiveView: CustomView,
		discardingView?: boolean
	) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateTemplatesListCurrentActiveView => {
			const currentActiveView =
				getState().templatesList.currentActiveView;
			const prevViewData = getState().templatesList.viewList?.data.find(
				view => view.id === selectedCurrentActiveView?.id
			);

			const isSameView =
				currentActiveView?.id === selectedCurrentActiveView?.id;

			if (isSameView && prevViewData?.id) {
				const hasFiltersChanged = areFiltersChanged(
					prevViewData.filter_criteria,
					selectedCurrentActiveView.filter_criteria
				);
				const hasColumnsChanged =
					JSON.stringify(selectedCurrentActiveView.columns) !==
					JSON.stringify(prevViewData.columns);

				return dispatch({
					type: TemplatesListActionTypes.UPDATE_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW,
					currentActiveView: selectedCurrentActiveView,
					isViewChanged: discardingView
						? false
						: hasFiltersChanged || hasColumnsChanged
				});
			}

			return dispatch({
				type: TemplatesListActionTypes.UPDATE_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW,
				currentActiveView: selectedCurrentActiveView,
				isViewChanged: false
			});
		};
	},

	resetCurrentActiveView: () => {
		return (dispatch: Dispatch): ResetTemplatesListCurrentActiveView =>
			dispatch({
				type: TemplatesListActionTypes.RESET_TEMPLATES_LIST_CURRENT_ACTIVE_VIEW
			});
	},

	createTemplatesListView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<CreateTemplatesListViewSuccessful> => {
			try {
				const response = await createViewForEntity(view);
				const successful: CreateTemplatesListViewSuccessful = {
					type: TemplatesListActionTypes.CREATE_TEMPLATES_LIST_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	updateTemplatesListView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateTemplatesListViewSuccessful> => {
			try {
				const response = await updateViewForEntity(view);
				const successful: UpdateTemplatesListViewSuccessful = {
					type: TemplatesListActionTypes.UPDATE_TEMPLATES_LIST_VIEW_SUCCESSFUL,
					view: response
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	},

	deleteTemplatesListView: (deleteView: CustomView, initialView: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteTemplatesListViewSuccessful> => {
			try {
				await deleteViewForEntity(deleteView.id);
				const successful: DeleteTemplatesListViewSuccessful = {
					type: TemplatesListActionTypes.DELETE_TEMPLATES_LIST_VIEW_SUCCESSFUL,
					deletedView: deleteView,
					initialView
				};
				return dispatch(successful);
			} catch (error) {
				console.error(error);
				return Promise.reject(error);
			}
		};
	}
};
